/*
 * Bundle builder
 *
 * Client-side state for the "build your own bundle" section. It only *previews*
 * the tiered saving — the actual money off is applied by a Shopify automatic
 * discount (or an auto-applied discount code), so the totals shown here are
 * always an estimate until the cart recalculates.
 */

if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();

        this.config = this.readConfig();
        this.tiers = (this.config.tiers || []).slice().sort((a, b) => a.min - b.min);
        this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
        this.itemTemplate = this.querySelector('[data-bundle-item-template]');
        this.mobileBar = this.querySelector('[data-bundle-mobile-bar]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.cards.forEach((card) => this.setupCard(card));

        this.querySelectorAll('[data-bundle-submit]').forEach((button) =>
          button.addEventListener('click', this.onSubmit.bind(this))
        );

        this.render();
      }

      readConfig() {
        const script = this.querySelector('[data-bundle-config]');

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('bundle-builder: invalid config', error);
          return { tiers: [], strings: {} };
        }
      }

      /* Setup */

      setupCard(card) {
        const select = card.querySelector('[data-bundle-variant]');
        const input = card.querySelector('[data-bundle-qty]');

        card.querySelector('[data-bundle-add]')?.addEventListener('click', () => {
          this.setQuantity(card, this.getQuantity(card) + 1);
          card.querySelector('[data-bundle-qty]')?.focus();
        });

        card.querySelector('[data-bundle-minus]')?.addEventListener('click', () => {
          this.setQuantity(card, this.getQuantity(card) - 1);
        });

        card.querySelector('[data-bundle-plus]')?.addEventListener('click', () => {
          this.setQuantity(card, this.getQuantity(card) + 1);
        });

        input?.addEventListener('change', () => {
          this.setQuantity(card, parseInt(input.value, 10) || 0);
        });

        select?.addEventListener('change', () => {
          // Carry the chosen quantity over to the newly selected variant, but
          // clamp it to whatever that variant can actually fulfil.
          this.setQuantity(card, this.getQuantity(card));
          this.render();
        });
      }

      /* Card state — the DOM is the source of truth, so nothing can desync. */

      getQuantity(card) {
        return parseInt(card.querySelector('[data-bundle-qty]')?.value, 10) || 0;
      }

      getSelectedOption(card) {
        const control = card.querySelector('[data-bundle-variant]');
        if (!control) return null;
        if (control.tagName === 'SELECT') return control.options[control.selectedIndex] || null;
        return control;
      }

      getCardData(card) {
        const option = this.getSelectedOption(card);
        if (!option) return null;

        const variantMax = parseInt(option.dataset.max, 10);
        const configMax = this.config.maxPerProduct > 0 ? this.config.maxPerProduct : Infinity;

        return {
          card,
          id: option.dataset.variantId,
          price: parseInt(option.dataset.price, 10) || 0,
          available: option.dataset.available === 'true',
          variantTitle: option.dataset.variantTitle || '',
          max: Math.min(Number.isNaN(variantMax) ? Infinity : variantMax, configMax),
          title: card.dataset.productTitle || '',
          image: card.dataset.productImage || '',
          quantity: this.getQuantity(card),
        };
      }

      setQuantity(card, quantity) {
        const data = this.getCardData(card);
        if (!data) return;

        let next = Math.max(0, quantity);
        if (!data.available) next = 0;
        next = Math.min(next, data.max);

        // Never let a single card push the bundle past the overall cap.
        if (this.config.maxTotal > 0) {
          const others = this.getTotalQuantity() - data.quantity;
          next = Math.min(next, Math.max(0, this.config.maxTotal - others));
        }

        const input = card.querySelector('[data-bundle-qty]');
        if (input) input.value = Math.max(0, next);

        this.render();
      }

      getSelection() {
        return this.cards
          .map((card) => this.getCardData(card))
          .filter((data) => data && data.quantity > 0 && data.available);
      }

      getTotalQuantity() {
        return this.cards.reduce((total, card) => {
          const data = this.getCardData(card);
          return data && data.available ? total + data.quantity : total;
        }, 0);
      }

      /* Tiers */

      getActiveTier(count) {
        return this.tiers.reduce((active, tier) => (count >= tier.min ? tier : active), null);
      }

      getNextTier(count) {
        return this.tiers.find((tier) => count < tier.min) || null;
      }

      tierLabel(tier) {
        if (!tier) return '';
        return tier.label && tier.label.length ? tier.label : `${tier.percent}%`;
      }

      /* Render */

      render() {
        const selection = this.getSelection();
        const count = selection.reduce((total, item) => total + item.quantity, 0);
        const subtotal = selection.reduce((total, item) => total + item.price * item.quantity, 0);
        const activeTier = this.getActiveTier(count);
        const nextTier = this.getNextTier(count);
        const discount = activeTier ? Math.round((subtotal * activeTier.percent) / 100) : 0;

        this.renderCards(count);
        this.renderProgress(count, activeTier, nextTier);
        this.renderItems(selection);
        this.renderTotals(count, subtotal, discount, activeTier);
        this.renderSubmit(count, subtotal - discount, activeTier);
      }

      renderCards(count) {
        const capReached = this.config.maxTotal > 0 && count >= this.config.maxTotal;

        this.cards.forEach((card) => {
          const data = this.getCardData(card);
          if (!data) return;

          const stepper = card.querySelector('[data-bundle-stepper]');
          const addButton = card.querySelector('[data-bundle-add]');
          const badge = card.querySelector('[data-bundle-card-count]');
          const price = card.querySelector('[data-bundle-card-price]');
          const minus = card.querySelector('[data-bundle-minus]');
          const plus = card.querySelector('[data-bundle-plus]');

          card.classList.toggle('is-selected', data.quantity > 0);
          card.classList.toggle('is-soldout', !data.available);

          if (stepper) stepper.hidden = data.quantity === 0;
          if (addButton) {
            addButton.hidden = data.quantity > 0;
            addButton.disabled = !data.available || capReached;
          }
          if (badge) badge.textContent = data.quantity;
          if (price) price.textContent = this.formatMoney(data.price);
          if (minus) minus.disabled = data.quantity <= 0;
          if (plus) plus.disabled = capReached || data.quantity >= data.max;
        });
      }

      renderProgress(count, activeTier, nextTier) {
        const lastTier = this.tiers[this.tiers.length - 1];
        const target = lastTier ? lastTier.min : 0;
        const percent = target > 0 ? Math.min(100, (count / target) * 100) : 0;

        this.querySelectorAll('[data-bundle-progress-fill]').forEach((fill) => {
          fill.style.width = `${percent}%`;
        });

        this.querySelectorAll('[data-bundle-tier]').forEach((element) => {
          const min = parseInt(element.dataset.min, 10) || 0;
          element.classList.toggle('is-reached', count >= min);
        });

        let message;
        if (nextTier) {
          message = this.interpolate(this.config.strings.messageNext, {
            quantity: nextTier.min - count,
            discount: this.tierLabel(nextTier),
          });
        } else if (activeTier) {
          message = this.interpolate(this.config.strings.messageMax, {
            quantity: count,
            discount: this.tierLabel(activeTier),
          });
        } else {
          message = '';
        }

        this.querySelectorAll('[data-bundle-message]').forEach((element) => {
          element.textContent = message;
          element.classList.toggle('is-unlocked', !nextTier && !!activeTier);
        });
      }

      renderItems(selection) {
        const lists = this.querySelectorAll('[data-bundle-items]');
        if (!lists.length || !this.itemTemplate) return;

        lists.forEach((list) => {
          list.innerHTML = '';

          selection.forEach((item) => {
            const node = this.itemTemplate.content.firstElementChild.cloneNode(true);
            const image = node.querySelector('[data-item-image]');

            if (image) {
              if (item.image) {
                image.src = item.image;
                image.alt = item.title;
              } else {
                image.remove();
              }
            }

            const variant = node.querySelector('[data-item-variant]');
            if (variant) {
              if (item.variantTitle) {
                variant.textContent = item.variantTitle;
              } else {
                variant.remove();
              }
            }

            const title = node.querySelector('[data-item-title]');
            if (title) title.textContent = item.title;

            const quantity = node.querySelector('[data-item-qty]');
            if (quantity) quantity.textContent = `${item.quantity} ×`;

            const price = node.querySelector('[data-item-price]');
            if (price) price.textContent = this.formatMoney(item.price * item.quantity);

            const remove = node.querySelector('[data-item-remove]');
            if (remove) {
              remove.setAttribute(
                'aria-label',
                this.interpolate(this.config.strings.removeItem, { title: item.title })
              );
              remove.addEventListener('click', () => this.setQuantity(item.card, 0));
            }

            list.appendChild(node);
          });
        });

        this.querySelectorAll('[data-bundle-empty]').forEach((element) => {
          element.hidden = selection.length > 0;
        });
      }

      renderTotals(count, subtotal, discount, activeTier) {
        this.setText('[data-bundle-count]', count);
        this.setText('[data-bundle-subtotal]', this.formatMoney(subtotal));
        this.setText('[data-bundle-discount]', `- ${this.formatMoney(discount)}`);
        this.setText('[data-bundle-total]', this.formatMoney(subtotal - discount));
        this.setText('[data-bundle-tier-label]', this.tierLabel(activeTier));

        this.querySelectorAll('[data-bundle-discount-row]').forEach((row) => {
          row.hidden = discount <= 0;
        });
      }

      renderSubmit(count, total, activeTier) {
        // With no tiers configured there is nothing to unlock, so never lock.
        const locked = this.config.requireMinTier && this.tiers.length > 0 && !activeTier;
        const nextTier = this.getNextTier(count);
        const minimum = this.tiers.length ? this.tiers[0].min : 1;

        this.querySelectorAll('[data-bundle-submit]').forEach((button) => {
          button.disabled = count === 0 || locked;

          const label = button.querySelector('[data-bundle-submit-text]');
          if (!label) return;

          if (count === 0) {
            label.textContent = this.config.strings.ctaEmpty;
          } else if (locked) {
            label.textContent = this.interpolate(this.config.strings.ctaLocked, {
              quantity: Math.max(0, minimum - count),
              discount: this.tierLabel(nextTier),
            });
          } else {
            label.textContent = this.config.strings.cta;
          }
        });

        this.setText('[data-bundle-mobile-total]', this.formatMoney(total));
        if (this.mobileBar) this.mobileBar.classList.toggle('is-visible', count > 0);
      }

      setText(selector, value) {
        this.querySelectorAll(selector).forEach((element) => {
          element.textContent = value;
        });
      }

      interpolate(template, values) {
        if (!template) return '';
        return Object.keys(values).reduce(
          (text, key) => text.replaceAll(`[${key}]`, values[key]),
          template
        );
      }

      /* Add to cart */

      onSubmit(event) {
        const button = event.currentTarget;
        if (button.disabled) return;

        const selection = this.getSelection();
        if (!selection.length) return;

        this.setError('');
        this.setLoading(true);

        const body = {
          items: selection.map((item) => {
            const line = { id: item.id, quantity: item.quantity };
            if (this.config.lineProperty) {
              line.properties = { [this.config.lineProperty]: this.config.linePropertyValue };
            }
            return line;
          }),
        };

        const redirects = this.getRedirectUrl(selection);

        if (!redirects && this.cart) {
          body.sections = this.cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',');
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.body = JSON.stringify(body);

        fetch(window.routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(response.description || response.message);
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: response });

            if (redirects) {
              window.location = redirects;
              return;
            }

            if (this.cart) {
              this.cart.classList.remove('is-empty');
              const items = response.items || [];
              this.cart.renderContents({
                ...response,
                key: items.length ? items[items.length - 1].key : response.key,
              });
              this.reset();
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch((error) => {
            console.error(error);
            this.setError(this.config.strings.error);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      /*
       * Returns the URL to send the shopper to after a successful add, or an
       * empty string when we should stay on the page. Discount-code mode always
       * redirects, because the code is applied by visiting /discount/CODE.
       */
      getRedirectUrl(selection) {
        const root = window.Shopify?.routes?.root || '/';
        const count = selection.reduce((total, item) => total + item.quantity, 0);
        const tier = this.getActiveTier(count);
        const destination = this.config.afterAdd === 'checkout' ? `${root}checkout` : window.routes.cart_url;

        if (this.config.discountCodeMode && tier && tier.code) {
          return `${root}discount/${encodeURIComponent(tier.code)}?redirect=${encodeURIComponent(destination)}`;
        }

        if (this.config.afterAdd === 'notification' && this.cart) return '';

        return destination;
      }

      reset() {
        this.cards.forEach((card) => {
          const input = card.querySelector('[data-bundle-qty]');
          if (input) input.value = 0;
        });
        this.render();
      }

      setLoading(loading) {
        this.querySelectorAll('[data-bundle-submit]').forEach((button) => {
          button.classList.toggle('loading', loading);
          button.setAttribute('aria-busy', loading);
          button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        });
      }

      setError(message) {
        this.querySelectorAll('[data-bundle-error]').forEach((element) => {
          element.textContent = message || '';
          element.hidden = !message;
        });
      }

      /* Money — mirrors the shop's money_format so totals match the cart. */

      formatMoney(cents) {
        const format = this.config.moneyFormat || '${{amount}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return format;

        const withDelimiters = (precision, thousands = ',', decimal = '.') => {
          if (isNaN(cents) || cents === null) return '0';
          const fixed = (cents / 100.0).toFixed(precision);
          const parts = fixed.split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return parts[1] ? whole + decimal + parts[1] : whole;
        };

        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = withDelimiters(0);
            break;
          case 'amount_with_comma_separator':
            value = withDelimiters(2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withDelimiters(0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = withDelimiters(2, "'", '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = withDelimiters(0, ' ');
            break;
          case 'amount_with_space_separator':
            value = withDelimiters(2, ' ', ',');
            break;
          default:
            value = withDelimiters(2);
        }

        return format.replace(placeholder, value);
      }
    }
  );
}
